'use client';

import type IUploadService from './IUploadService';
import type { AbortableRequest } from '../Utils/FetchUtils';
import FetchUtils from '../Utils/FetchUtils';
import { useContext } from 'react';
import React from 'react';

export default class S3ObjectStorageService implements IUploadService {
	constructor(private readonly _supportsCors: boolean) {}

	public getPresignedUrlForUpload(fileName: string, file: File): AbortableRequest<string> {
		const request = FetchUtils.getJson<{ url: string }>(
			`/api/storage/upload?filename=${encodeURIComponent(fileName)}&type=${encodeURIComponent(file.type)}`,
		);

		return {
			abort: request.abort,
			response: new Promise((resolve, reject) => {
				request.response
					.then((response) => {
						resolve(response.url);
					})
					.catch(reject);
			}),
		};
	}

	public getPresignedUrlForDownload(fileName: string): AbortableRequest<string> {
		const request = FetchUtils.getJson<{ url: string }>(
			`/api/storage/download?filename=${encodeURIComponent(fileName)}`,
		);
		return {
			abort: request.abort,
			response: new Promise((resolve, reject) => {
				request.response
					.then((response) => {
						resolve(response.url);
					})
					.catch(reject);
			}),
		};
	}

	public getRedirectUrl(fileName: string) {
		return `/api/storage/redirect?filename=${encodeURIComponent(fileName)}`;
	}

	public getDownloadUrl(fileName: string): string {
		return `/api/storage/download/${fileName}`;
	}

	public uploadFile(fileName: string, file: File): AbortableRequest<string> {
		if (this._supportsCors) {
			return this.uploadViaPresignedUrl(fileName, file);
		} else {
			return this.uploadViaServer(fileName, file);
		}
	}

	private uploadViaServer(fileName: string, file: File): AbortableRequest<string> {
		const formData = new FormData();
		formData.append('file', file, fileName);
		formData.append('filename', fileName);
		formData.append('type', file.type);

		const request = FetchUtils.execute(`/api/storage/upload`, {
			method: 'POST',
			body: formData,
		});

		return {
			abort: request.abort,
			response: new Promise((resolve, reject) => {
				request.response
					.then(() => {
						resolve(this.getRedirectUrl(fileName));
					})
					.catch(reject);
			}),
		};
	}

	private uploadViaPresignedUrl(fileName: string, file: File): AbortableRequest<string> {
		const urlRequest = this.getPresignedUrlForUpload(fileName, file);

		const aborts = [urlRequest.abort];

		return {
			abort: () => {
				aborts.forEach((abort) => abort());
			},
			response: new Promise((resolve, reject) => {
				urlRequest.response
					.then((url) => {
						const uploadRequest = FetchUtils.execute(url, {
							method: 'PUT',
							body: new Blob([file], { type: file.type }),
							headers: {
								'Content-Type': file.type,
							},
						});

						aborts.push(uploadRequest.abort);

						uploadRequest.response
							.then(() => {
								resolve(this.getRedirectUrl(fileName));
							})
							.catch(reject);
					})
					.catch(reject);
			}),
		};
	}
}

export const S3ObjectStoreContext = React.createContext<S3ObjectStorageService | null>(null);

export function useS3ObjectStorageService(): S3ObjectStorageService {
	const s3ObjectStorageService = useContext(S3ObjectStoreContext);
	if (!s3ObjectStorageService) {
		throw new Error('S3ObjectStorageService is not provided in the context');
	}
	return s3ObjectStorageService;
}
